import React, { useState, useEffect } from 'react'
import { Badge, Tooltip, Button } from 'antd'
import { PhoneOutlined, EyeOutlined } from '@ant-design/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { useWebSocket } from '../../contexts/WebSocketContext'

interface LiveCallIndicatorProps {
  collapsed?: boolean
}

export const LiveCallIndicator: React.FC<LiveCallIndicatorProps> = ({ collapsed = false }) => {
  const { activeCalls, isConnected } = useWebSocket()
  const [pulse, setPulse] = useState(false)

  useEffect(() => {
    if (activeCalls.length > 0) {
      setPulse(true)
      const timer = setTimeout(() => setPulse(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [activeCalls.length])

  const handleViewCalls = () => {
    window.location.href = '/dashboard/calls'
  }

  if (collapsed) {
    return (
      <Tooltip title={`${activeCalls.length} live calls`} placement="right">
        <motion.div
          animate={pulse ? { scale: [1, 1.1, 1] } : {}}
          transition={{ duration: 1 }}
          className="flex justify-center"
        >
          <Badge 
            count={activeCalls.length} 
            size="small"
            className="cursor-pointer"
            onClick={handleViewCalls}
          >
            <Button
              type="text"
              icon={<PhoneOutlined />}
              className={`
                text-white 
                ${activeCalls.length > 0 ? 'text-green-500' : 'text-gray-400'}
                hover:bg-white/10
              `}
              size="large"
            />
          </Badge>
        </motion.div>
      </Tooltip>
    )
  }

  return (
    <motion.div
      animate={pulse ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 1 }}
      className="space-y-3"
    >
      {/* Connection Status */}
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-xs text-gray-400">
          {isConnected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      {/* Live Calls Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneOutlined className="text-green-500" />
          <span className="text-white font-medium">Live Calls</span>
        </div>
        <Badge count={activeCalls.length} size="small" />
      </div>

      {/* Active Calls List */}
      <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
        <AnimatePresence>
          {activeCalls.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs text-gray-500 text-center py-2"
            >
              No active calls
            </motion.div>
          ) : (
            activeCalls.map((call) => (
              <motion.div
                key={call.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-2 bg-white/5 rounded-sm border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                onClick={handleViewCalls}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-medium truncate">
                      {call.callerName || call.callerNumber}
                    </div>
                    <div className="text-xs text-gray-400">
                      {Math.floor(call.duration / 60)}:{(call.duration % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${
                      call.status === 'in-progress' ? 'bg-green-500' :
                      call.status === 'ringing' ? 'bg-yellow-500' :
                      call.status === 'on-hold' ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`} />
                    {call.language && (
                      <span className="text-xs text-gray-400 uppercase">
                        {call.language.slice(0, 2)}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* View All Button */}
      {activeCalls.length > 0 && (
        <Button
          type="text"
          icon={<EyeOutlined />}
          size="small"
          className="w-full text-blue-400 hover:bg-blue-500/20 border-blue-500/30"
          onClick={handleViewCalls}
        >
          View All Calls
        </Button>
      )}
    </motion.div>
  )
}
